import { Alert, Button, Grid, InputAdornment, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableRow, TextField, useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/userContext';
import hash from 'object-hash';
import { MatriculasType, useGetMatriculas, useSetMatriculas } from '../../hooks/Matriculas';


export function Matriculas() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useContext(UserContext);

  const currentYear = dayjs().year();
  const currentMonth = dayjs().month();

  const [values, setValues] = useState<{ [key: number]: (number | null)[] }>();
  const [originalValues, setOriginalValues] = useState<{ [key: number]: (number | null)[] }>();

  const [mutableMatriculas, setMutableMatriculas] = useState<MatriculasType[]>();

  useSetMatriculas(mutableMatriculas, user);
  const matriculasResult = useGetMatriculas(user);

  useEffect(() => {
    setTimeout(() => matriculasResult.refetch(), 500);
  }, [mutableMatriculas]);

  const monthsHeader = [
    '',
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  const matriculas = matriculasResult?.data;

  const olderRecord = matriculas?.length ? matriculas.reduce((prev, curr) => {
    return (curr.ano < prev.ano) ? curr : prev;
  }).ano : dayjs().year();

  const listingYears:(number | never)[] = [];
  for (let year = olderRecord; year <= currentYear + 1; year++) listingYears.push(year);

  // Fill values

  const fillValuesWithLoadedData = () => {
    if (!matriculas) return;
    const mutableValues:any = {};
    for (const y of listingYears) {
      mutableValues[y] = [];
      for (let mes = 1; mes <= 12; mes++) {
        const cuotaEncontrada = matriculas.find((c) => c.ano === y && c.mes === mes);
        if (cuotaEncontrada) mutableValues[y].push(cuotaEncontrada.monto);
        else mutableValues[y].push(null);
      }
    }
    setOriginalValues(mutableValues);
    setValues(mutableValues);
  };

  useEffect(() => {
    fillValuesWithLoadedData();
  }, [matriculas]);

  const handleSubmit = () => {
    if (!matriculas || !values) return;
    const allMatriculas:MatriculasType[] = [];
    for (const year in values) {
      values[year].forEach((value, index) => {
        const mes = index + 1;
        if (value) {
          const existe = matriculas.find((c) => c.ano === Number(year) && c.mes ===  mes);
          const modificada = existe?.monto !== value;
          if (!existe || modificada) allMatriculas.push({ ano: Number(year), mes, monto: value });
        }
      });
    }
    setMutableMatriculas(allMatriculas);
  };

  const faltanMatriculas = !values?.[currentYear][currentMonth]
  || !values?.[currentYear][currentMonth + 1]
  || (currentMonth === 12 && !values?.[currentYear + 1][1]);

  if (!user?._id) return null;

  if (!matriculasResult.data || matriculasResult.isLoading || !values) return <LinearProgress />;

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={10}
        style={{ position: 'relative',
          padding: 16,
          height: isMobile ? 'calc(100vh - 60px)' : 'calc(100vh - 285px)' }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="simple table" >
          <TableBody>
            <TableRow key="months" >
              {monthsHeader?.map((month) => (<TableCell key={`${month}-table-header`} style={{ borderStyle: 'solid' }}>{month}</TableCell>))}
            </TableRow>
            {listingYears?.map((year) => (
              <TableRow
                key={`${year}-table-header`}
                style={{ borderStyle: 'solid' }}
              >
                <TableCell style={{ borderStyle: 'solid' }}>{year}</TableCell>
                {values[year]?.map((v, mes) => {
                  const twoDigitsMonth = String(mes + 1).length < 2 ? String(mes + 1).padStart(2, '0') : String(mes + 1);
                  const esPasado = dayjs(`01-${twoDigitsMonth}-${year}`, 'DD-MM-YYYY').isBefore(dayjs(), 'days');
                  const esFuturo = dayjs(`01-${twoDigitsMonth}-${year}`, 'DD-MM-YYYY').isAfter(dayjs().add(2, 'months'), 'days');
                  let color = 'rgb(200 200 200)'; // disable
                  if (!esPasado && v) color = 'rgb(150 213 179)';
                  if (!v) color = 'rgb(220 165 165)';
                  if (esFuturo && !v) color = '#d2e0ef';

                  return (
                    <TableCell  key={`table-cell-${year}-${twoDigitsMonth}`} style={{ borderStyle: 'solid', backgroundColor: color, padding: 4 }}>
                      <TextField
                        margin="dense"
                        key={`monto${year}-${twoDigitsMonth}`}
                        type="number"
                        fullWidth
                        variant="standard"
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><span  style={{ fontSize: 12 }}>$</span></InputAdornment>,
                          style: { fontSize: 12 },
                        }}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          setValues((prev) => {
                            const mutable = JSON.parse(JSON.stringify(prev ?? {}));
                            mutable[year][mes] = Number(event.target.value);
                            return mutable;
                          });
                        }}
                        value={v || ''}
                      />
                    </TableCell>
                  );
                },
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div style={{ position: 'absolute', bottom: 10, right: 10, width: '100%', paddingLeft: 24 }}>
          <Grid container>
            <Grid
              item
              xs={12}
              md={6}
            >
              {faltanMatriculas &&
              <Alert severity="error" style={{ width: '100%', textAlign: 'left' }}>
                  Faltan cargar los valores de las matriculas de este mes o el mes próximo!
              </Alert>
              }
            </Grid>
            <Grid
              item
              container
              xs={12}
              md={6}
              style={{
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                disabled={hash(originalValues ?? {}) === hash(values)}
                variant='contained'
                onClick={handleSubmit}
                style={{ marginRight: 10 }}
              >
                Guardar
              </Button>
              <Button
                disabled={hash(originalValues ?? {}) === hash(values)}
                variant='contained'
                onClick={() => fillValuesWithLoadedData() }
              >
                Descartar
              </Button>
            </Grid>
          </Grid>
        </div>
      </TableContainer>
    </>
  );
}
